import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { OverlayTrigger, Tooltip, ToggleButton } from 'react-bootstrap';
import { ReactComponent as ZoomInIcon } from "../../assets/img/icons/zoom-in.svg";
import { ReactComponent as ZoomOutIcon } from "../../assets/img/icons/zoom-out.svg";
import moment from 'moment';
import ClassCard from '../ClassCard';
import { getBackgroundColor } from '../../utilities';
import { weekDays, TIME_SLOTS, DAY_MIN_WIDTH, COLOR_BY_STORAGE_KEY } from '../../constants/variables';
import styles from './index.module.scss';
import { useParams } from "react-router-dom";
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import adaptivePlugin from '@fullcalendar/adaptive';
import scrollgridPlugin from '@fullcalendar/scrollgrid';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import { selectAll as selectAllStudio } from '../../redux/slices/studio.slice';
import { addChosenSticker } from "../../redux/slices/stickers.slice";
import { modalEdit } from "../../redux/slices/modal.slice";
import { useReactToPrint } from "react-to-print";
import ColorBySelect from '../ColorBySelect';
import { ReactComponent as FilterIcon } from "../../assets/img/icons/filterList.svg";
import { DialogModal } from "../FilterDialogModal";
import { filterSetAll } from "../../redux/slices/scheduleFilters.slice";
import Select, { components as SelectComponents } from "react-select";
import { ReactComponent as ScheduleIcon } from "../../assets/img/icons/schedule.svg";
import { selectAll as selectAllBoards, addChosenBoard } from "../../redux/slices/boards.slice";
import ExportPanel from "../ExportPanel";

const DEFAULT_CARD_HEIGHT = 28;
const STICKER_STYLE_TOP_RATIO = 7;

const WeekView = ({ board, onClose, colorBy: propColorBy, onColorByChange, onBoardChange }) => {
    const dispatch = useDispatch();
    const storedEvents = useSelector((state) => state.events.data);
    const { isAdmin, id } = useSelector(state => state.personality.user);
    const allStudio = useSelector(selectAllStudio);
    const allBoards = useSelector(selectAllBoards);
    const boardOptions = allBoards.map(b => ({ ...b, value: b.id, label: b.boardName }));
    const params = useParams();
    const calendarRef = useRef(null);
    
    const storedFilters = useSelector(state => state.scheduleFilters.filters);
    const [filterModal, setFilterModal] = useState(false);

    const [localColorBy, setLocalColorBy] = useState(() => {
      const savedColorBy = localStorage.getItem(COLOR_BY_STORAGE_KEY);
      return savedColorBy || propColorBy || 'levels';
    });
    const currentColorBy = propColorBy !== undefined ? propColorBy : localColorBy;

    const [currentWeek] = useState(moment());
    const [weekEvents, setWeekEvents] = useState([]);
    const [slotTime, setSlotTime] = useState(TIME_SLOTS.MINUTES_15);
    const [dayMinWidth, setDayMinWidth] = useState(DAY_MIN_WIDTH.LITTLE_WIDTH);
    const [loading] = useState(false);
    const [, setPrintMode] = useState(false);
    const [slotMinTime, setSlotMinTime] = useState("06:00");
    const [slotMaxTime, setSlotMaxTime] = useState("23:00");
    const printConfigRef = useRef({ maxColumnsPerPage: 13, pageOrientation: 'portrait', marginBottom: '0.44in' });

    // Export panel state


    const activeFilters = useMemo(() => {
        let activeFilters = {};
        if (storedFilters) {
            for (let [key, value] of Object.entries(storedFilters)) {
                activeFilters[key] = value.filter((item) => item?.checked === true)
                if (activeFilters[key].length === 0) {
                    delete activeFilters[key]
                }
            }
        }

        return activeFilters
    }, [storedFilters]);

    // Business hours per day — drives gray overlay and drop constraint in FullCalendar
    const businessHours = useMemo(() => {
        if (!storedEvents?.boardDays) return [];
        const fcDayMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
        return weekDays.map(day => {
            const d = storedEvents.boardDays[day];
            if (!d?.start || !d?.end) return null;
            return { daysOfWeek: [fcDayMap[day]], startTime: d.start, endTime: d.end };
        }).filter(Boolean);
    }, [storedEvents]);

    // Get week start date
    const weekStart = currentWeek.clone().startOf('week').add(1, 'days');
    const weekStartKey = weekStart.format('YYYY-MM-DD');
    // Generate week days
    const [weekDates, hiddenDays] = useMemo(() => {
        const dates = [];
        const boards = storedEvents['boardDays'] || [];
        let hiddenDays = [];
        for (let i = 0; i < 7; i++) {
            const date = weekStart.clone().add(i, 'days');
            if (boards[weekDays[i]] && boards[weekDays[i]].start && boards[weekDays[i]].end) {
                dates.push({
                    date: date.format('YYYY-MM-DD'),
                    dayName: date.format('dddd'),
                    dayShort: date.format('ddd'),
                    dayNumber: date.format('D'),
                    month: date.format('MMM')
                });
            } else {
                let fullCalendarDay = i < 6 ? i+1 : 0;
                hiddenDays.push(fullCalendarDay);
            }
        }
        return [dates, hiddenDays];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekStartKey]);
    
    // Create studios array for FullCalendar
    const studios = useMemo(() => {
        return allStudio.map(item => ({ ...item, title: item.studioName }));
    }, [allStudio]);
    // Load events for the current week
    useEffect(() => {
        if (board?.id) {
            dispatch({
                type: "EVENTS_SET_WEEK",
                query: {
                    userId: isAdmin ? params?.id : id,
                    boardId: board.id
                }
            })
        }
    }, [board, dispatch, id, isAdmin, params?.id]);
    
    useEffect(() => {
        if (!calendarRef.current) return;
        
        const calendarEl = calendarRef.current;
        const addDaySeparators = () => {
            // Function to process elements with data-date in a specific area
            const processArea = (container) => {
                // Find all cells with data-date in this area
                const cellsWithDate = container.querySelectorAll('[data-date]');
                
                // Group by date
                const cellsByDate = {};
                cellsWithDate.forEach(cell => {
                    const date = cell.getAttribute('data-date');
                    if (!cellsByDate[date]) {
                        cellsByDate[date] = [];
                    }
                    cellsByDate[date].push(cell);
                });
                
                // Remove previous classes in this area
                cellsWithDate.forEach(cell => {
                    cell.classList.remove('day-separator');
                });
                
                // Find unique dates in order of appearance
                const uniqueDates = Array.from(new Set(
                    Array.from(cellsWithDate)
                        .map(cell => cell.getAttribute('data-date'))
                        .filter(date => date)
                ));
                
                // Add class to the last element of each date group
                uniqueDates.forEach((date, index) => {
                    if (index < uniqueDates.length - 1) { // Not the last day
                        const dateCells = cellsByDate[date] || [];
                        // Find the last element of this group (last resource of the day)
                        const lastCellInGroup = dateCells[dateCells.length - 1];
                        if (lastCellInGroup) {
                            lastCellInGroup.classList.add('day-separator');
                        }
                    }
                });
            };
            
            // Process headers (fc-col-header)
            const headerContainer = calendarEl.querySelector('.fc-col-header');
            if (headerContainer) {
                // Process the first row of headers (weekdays) separately
                const firstRow = headerContainer.querySelector('thead tr:first-child');
                if (firstRow) {
                    // Find weekday cells (with data-date but without data-resource-id)
                    const dayCells = Array.from(firstRow.querySelectorAll('.fc-col-header-cell[data-date]:not([data-resource-id])'));
                    
                    // Remove previous classes
                    dayCells.forEach(cell => {
                        cell.classList.remove('day-separator');
                    });
                    
                    // Add separator to the last cell of each day (except the last day)
                    dayCells.forEach((cell, index) => {
                        if (index < dayCells.length - 1) {
                            cell.classList.add('day-separator');
                        }
                    });
                }
                
                // Process remaining header rows (studios) - only elements with data-resource-id
                const resourceCells = headerContainer.querySelectorAll('[data-date][data-resource-id]');
                if (resourceCells.length > 0) {
                    // Group by date
                    const cellsByDate = {};
                    resourceCells.forEach(cell => {
                        const date = cell.getAttribute('data-date');
                        if (!cellsByDate[date]) {
                            cellsByDate[date] = [];
                        }
                        cellsByDate[date].push(cell);
                    });
                    
                    // Remove previous classes
                    resourceCells.forEach(cell => {
                        cell.classList.remove('day-separator');
                    });
                    
                    // Find unique dates in order of appearance
                    const uniqueDates = Array.from(new Set(
                        Array.from(resourceCells)
                            .map(cell => cell.getAttribute('data-date'))
                            .filter(date => date)
                    ));
                    
                    // Add class to the last element of each date group
                    uniqueDates.forEach((date, index) => {
                        if (index < uniqueDates.length - 1) { // Not the last day
                            const dateCells = cellsByDate[date] || [];
                            // Find the last element of this group (last resource of the day)
                            const lastCellInGroup = dateCells[dateCells.length - 1];
                            if (lastCellInGroup) {
                                lastCellInGroup.classList.add('day-separator');
                            }
                        }
                    });
                }
            }
            
            // Process calendar body (fc-timegrid-cols)
            const timegridCols = calendarEl.querySelector('.fc-timegrid-cols');
            if (timegridCols) {
                processArea(timegridCols);
            }
        };
        
        // Add separators after rendering
        const timeoutId = setTimeout(addDaySeparators, 100);
        
        // Also add when events change
        const observer = new MutationObserver(addDaySeparators);
        observer.observe(calendarEl, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-date']
        });
        
        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, [weekEvents, studios]);
    
    // Process events for week view using FullCalendar format
    useEffect(() => {
        if (storedEvents) {
            let processedEvents = [];

            const boards = storedEvents['boardDays'] || [];

            // Find min start time and max end time across all days
            let minStart = null;
            let maxEnd = null;

            weekDays.forEach(day => {
                const startTime = moment(boards[day].start, 'H:mm');
                if (!minStart || startTime.isBefore(minStart)) {
                    minStart = startTime.clone();
                }
                const endTime = moment(boards[day].end, 'H:mm');
                if (!maxEnd || endTime.isAfter(maxEnd)) {
                    maxEnd = endTime.clone();
                }
            });

            // Set slot times (default to 06:00 and 23:00 if no events found)
            if (minStart) {
                setSlotMinTime(minStart.format('HH:mm'));
            } else {
                setSlotMinTime("06:00");
            }

            if (maxEnd) {
                setSlotMaxTime(maxEnd.format('HH:mm'));
            } else {
                setSlotMaxTime("23:00");
            }

            weekDates.forEach(dateObj => {
                const dayName = dateObj.dayName.charAt(0).toUpperCase() + dateObj.dayName.slice(1);
                const dayEvents = storedEvents['studios'] || [];
                dayEvents.forEach(studio => {
                    if (studio.scheduleSticker) {
                        studio.scheduleSticker.forEach(sticker => {
                            if (dayName !== sticker.dayOfTheWeek) {
                                return;
                            }
                            processedEvents.push({
                                id: sticker.id,
                                resourceId: sticker.studioId,
                                className: sticker.className,
                                title: sticker.className || "None",
                                length: sticker.length,
                                start: moment(`${dateObj.date} ${sticker.start}`, 'YYYY-MM-DD HH:mm:ss').format(),
                                end: moment(`${dateObj.date} ${sticker.start}`, 'YYYY-MM-DD HH:mm:ss').add(sticker.length, "hours").format(),
                                background: getBackgroundColor(sticker, propColorBy),
                                styles: sticker.styles,
                                levels: sticker.levels,
                                teachers: sticker.teachers,
                                teachersId: sticker.teachersId,
                                levelsId: sticker.levelsId,
                                students: sticker.students || [],
                                studentIds: (sticker.students || []).map(s => s.id),
                                extendedProps: {
                                    weekDatesCount: weekDates.length,
                                },
                            });
                        });
                    }
                });
            });

            if (Object.keys(activeFilters).length > 0) {
                processedEvents = processedEvents.filter((item) => {
                    let flag = true;
                    for (let key of Object.keys(activeFilters)) {
                    if (key === "length") {
                        flag = activeFilters[key].find(el => el.value === item[key]);
                        return !!flag
                    } else {
                        flag = item[key] !== null && !!(activeFilters[key].find(el => item[key].includes(el.value)));
                    }
                    if (flag === false) return !!flag
                }
                    return true;
                });
            }
            
            setWeekEvents(processedEvents);
        }
    }, [storedEvents, weekDates, propColorBy, activeFilters]);

    const onFilterRemove = useCallback((filterItem) => {
        const updatedFilters = JSON.parse(JSON.stringify(storedFilters));
        updatedFilters[filterItem.entity] = updatedFilters[filterItem.entity].map(item => {
            if (item.value === filterItem.value) {
                item.checked = false;
            }
            return item;
        });
        dispatch(filterSetAll(updatedFilters));
    }, [dispatch, storedFilters]);

    const filtersList = () => {
        const filterList = Object.values(activeFilters).flat();
        return (
            <div className="d-flex flex-wrap align-items-center mb-2">
                <span className="me-2 mb-1 fw-bold fs-6">
                    Filter ({filterList.length})
                </span>
                {filterList.map((item, index) => (
                    <div key={`week-filter-${index}`} className="selectedOption">
                        <span className="text-capitalize me-1">
                            {item.entity}:
                        </span>
                        {item.label}
                        <div
                            className="closeButton"
                            onClick={() => onFilterRemove(item)}
                        >
                            X
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const createEventBody = (event, oldEvent = null) => {
        const dayOfTheWeek = moment(event.start).format('dddd').charAt(0).toUpperCase() + moment(event.start).format('dddd').slice(1);
        return {
            scheduleId: event.id,
            id: event.id,
            length: event.extendedProps.length,
            className: event.extendedProps.className || event.title,
            start: moment(event.start).format("HH:mm"),
            end: moment(event.start).add(event.extendedProps.length, "hours").format("HH:mm"),
            styles: event.extendedProps.styles,
            levels: event.extendedProps.levels,
            teachers: event.extendedProps.teachers,
            teachersId: event.extendedProps.teachersId,
            levelsId: event.extendedProps.levelsId,
            students: event.extendedProps.students || [],
            studentIds: (event.extendedProps.students || []).map(s => s.id),
            studioId: event.getResources()[0].id,
            prevStudioId: oldEvent ? oldEvent.getResources()[0].id : event.getResources()[0].id,
            userId: isAdmin ? params?.id : id,
            isReusable: false,
            dayOfTheWeek: dayOfTheWeek,
            boardId: board.id
        };
    };

    const handleEventClick = ({ event }) => {
        const item = createEventBody(event);
        dispatch(addChosenSticker(item));
        dispatch(modalEdit('Events'));
    };

    const handleEvents = ({ event, oldEvent }) => {
        const body = createEventBody(event, oldEvent);
        let prevEvent = createEventBody(oldEvent);
        prevEvent = { ...prevEvent, studioId: oldEvent.getResources()[0].id, prevStudioId: event.getResources()[0].id };

        dispatch({ type: "EVENTS_UPDATE_ONE", payload: { body, prevEvent } });
    };

    const onChangeDayMinWidth = () => {
        setDayMinWidth(dayMinWidth === DAY_MIN_WIDTH.LITTLE_WIDTH ? DAY_MIN_WIDTH.BIG_WIDTH : DAY_MIN_WIDTH.LITTLE_WIDTH);
    };

    const handleColorByChange = (selectedOption) => {
        const newColorBy = selectedOption.value;
        setLocalColorBy(newColorBy);
        localStorage.setItem(COLOR_BY_STORAGE_KEY, newColorBy);
        
        if (onColorByChange) {
            onColorByChange(selectedOption);
        }
    };

    const handlePrint = useReactToPrint({
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                setSlotTime(TIME_SLOTS.MINUTES_15);
                setPrintMode(false);
                setTimeout(() => resolve(), 100);
            });
        },
        onAfterPrint: () => {
            setPrintMode(false);
        },
        content: () => {
            const baseClone = calendarRef.current.cloneNode(true);
            const headerLeftItem = baseClone.querySelectorAll('.fc-timegrid-axis-frame');

            headerLeftItem.forEach((el) => {
                const currentHeight = parseFloat(el.style.height || '0'); 
                el.style.height = `${currentHeight + currentHeight/6.66}px`;   
            });
            const header = baseClone.querySelectorAll('.fc-scrollgrid-sync-inner');

            header.forEach((el) => { 
                el.style.paddingLeft = `10px`;   
                el.style.paddingRight = `10px`;   
            });
            // Common styling (as in FiltersSidebar)
            const harness = baseClone.getElementsByClassName("fc-scroller-harness fc-scroller-harness-liquid")[0];
            if (harness) {
                harness.style.overflow = "visible";
                const tableTimeRows = baseClone.querySelectorAll('table tbody tr');
                if (tableTimeRows && tableTimeRows.length > 0) {
                    for (let i = 0; i < tableTimeRows.length; i++) {
                        tableTimeRows[i].style.height = '7mm';
                    }
                }
            }
            const scroller = baseClone.getElementsByClassName("fc-scroller fc-scroller-liquid-absolute")[0];
            if (scroller) {
                scroller.style.overflow = "visible";
            }

            // Ensure no vertical scroll and allow full-height printing across pages
            const toAutoSelectors = [
                '.fc-scroller-harness',
                '.fc-scroller-harness-liquid',
                '.fc-scroller',
                '.fc-scroller-liquid-absolute',
                '.fc-timegrid-body table',
                '.fc-scrollgrid',
                '.fc-scrollgrid-sync-table',
                '.fc-view-harness',
                '.fc-timegrid',
                '.fc-timegrid-slots',
                '.fc-timegrid-cols'
            ];

            toAutoSelectors.forEach((sel) => {
                baseClone.querySelectorAll(sel).forEach((el) => {
                    el.style.maxHeight = 'none';
                    el.style.overflow = 'visible';
                });
            });

            // Reduce header height to compact as in FiltersSidebar
            const headerEl = baseClone.querySelector('.fc-col-header');
            if (headerEl) {
                // headerEl.style.height = '7mm';
            }

            // Show only weekday names in the date header (no numeric dates)
            const dayHeaderCells = baseClone.querySelectorAll('.fc-col-header thead tr:first-child .fc-col-header-cell-cushion');

            if (dayHeaderCells && dayHeaderCells.length >= 7) {
                Array.from(dayHeaderCells).forEach((cell, index) => {
                    const d = weekStart.clone().add(index, 'days');
                    cell.textContent = d.format('dddd');
                    cell.style.textTransform = 'capitalize';
                    cell.style.fontWeight = '700';
                    cell.style.fontSize = '14px';
                });
            }


            // Adjust time slot row heights and borders
            const tableTimeRows = baseClone.querySelectorAll('.fc-timegrid-slots table tbody tr');

            if (tableTimeRows && tableTimeRows.length > 0) {
                let startTimeDayCell = tableTimeRows[0].querySelector('td');
                let startTimeDay = startTimeDayCell ? parseFloat(startTimeDayCell.getAttribute('data-time')) : 0;

                for (let i = 0; i < tableTimeRows.length; i++) {
                    tableTimeRows[i].style.height = '7mm';
                }
                // Hour lines only — clear ALL fc-timegrid-slot rows (label + grid cols), restore hourly
                baseClone.querySelectorAll('.fc-timegrid-slot').forEach((row) => {
                    const isMajor = !row.classList.contains('fc-timegrid-slot-minor');
                    row.querySelectorAll('td').forEach((cell) => {
                        cell.style.setProperty('border-top', isMajor ? '0.5px solid #d0d0d0' : 'none', 'important');
                        cell.style.setProperty('border-bottom', 'none', 'important');
                    });
                });

                // Border under studio name headers — box-shadow bypasses border-collapse.
                baseClone.querySelectorAll('.fc-col-header-cell').forEach(el => {
                    el.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });

                // Border at the very bottom of the grid.
                baseClone.querySelectorAll('.fc-timegrid-slots table tbody tr:last-child td').forEach(td => {
                    td.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });
                baseClone.querySelectorAll('.fc-timegrid-cols table tbody tr:last-child td').forEach(td => {
                    td.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });
                // Adjust stickers sizing/positioning
                const stickers = baseClone.getElementsByClassName('fc-timegrid-event-harness fc-timegrid-event-harness-inset');
                for (let i = 0; i < stickers.length; i++) {
                    const sticker = stickers[i].getElementsByClassName('test')[0];
                    if (!sticker) continue;
                    const lengthAttr = sticker.getAttribute('data-length');
                    const timeStartStickerAttr = sticker.getAttribute('data-time');
                    const length = lengthAttr ? parseFloat(lengthAttr) : 0;
                    const timeStartSticker = timeStartStickerAttr ? parseFloat(timeStartStickerAttr) : startTimeDay;
                    const innerDiv = sticker.querySelector('div');
                    if (innerDiv) innerDiv.style.overflow = 'hidden';

                    // Font: cap at 13px, scale down only if card height can't fit all items
                    {
                        // Select ALL card row divs (title, teacher, time).
                        // ClassCard: sticker > [aria-hidden overlays] + .card > [rows]
                        // The time row has no data-id so querySelectorAll('[data-id]') misses it.
                        // Skip aria-hidden overlays to reach the .card div, grab its DIV children.
                        const cardDiv = Array.from(sticker.children).find(
                            el => el.tagName === 'DIV' && !el.hasAttribute('aria-hidden')
                        );
                        const allCardItems = cardDiv
                            ? Array.from(cardDiv.children).filter(el => el.tagName === 'DIV')
                            : Array.from(sticker.querySelectorAll('[data-id="card-item"]'));
                        const numItems = Math.max(1, allCardItems.length);
                        const cardHeightMm = length * 28; // 4 slots/hr × 7mm/slot
                        const mmPerLine = 5.98; // 13px × 0.353mm/px × 1.3 lineHeight
                        const linesAvail = Math.max(0.5, (cardHeightMm - 2) / mmPerLine);
                        const rawSize = 13 * Math.min(1, linesAvail / numItems);
                        const printFontSize = `${Math.max(7.5, rawSize).toFixed(1)}px`;
                        const printLineH = rawSize >= 11 ? '1.3' : '1.2';
                        allCardItems.forEach((item) => {
                            item.style.setProperty('font-size', printFontSize, 'important');
                            item.style.setProperty('line-height', printLineH, 'important');
                        });
                    }
                    if (length >= 1.5) {
                        sticker.querySelectorAll('[data-id="card-item"]').forEach((item) => {
                            item.style.overflow = 'auto';
                            item.style.display = 'block';
                            item.style.webkitLineClamp = 'initial';
                            item.style.webkitBoxOrient = 'horizontal';
                            item.style.wordBreak = 'normal';
                        });
                    }

                    stickers[i].style.height = `${length * DEFAULT_CARD_HEIGHT}mm`;
                    const countPoint = (timeStartSticker > startTimeDay ? timeStartSticker - startTimeDay : startTimeDay - timeStartSticker) / 0.2495;
                    stickers[i].style.top = `${countPoint !== 0 ? (STICKER_STYLE_TOP_RATIO + ((countPoint - 1) * STICKER_STYLE_TOP_RATIO)) : 0}mm`;
                    stickers[i].style.bottom = '0mm';
                }
            }

            // Clip timegrid body to visible slot height — preserves internal grey, hides grey tail
            const timegridBody = baseClone.querySelector('.fc-timegrid-body');
            if (timegridBody && tableTimeRows.length > 0) {
                const totalMm = tableTimeRows.length * 7;
                timegridBody.style.height = `${totalMm}mm`;
                timegridBody.style.maxHeight = `${totalMm}mm`;
                timegridBody.style.overflow = 'hidden';
            }
            baseClone.querySelectorAll('.fc-timegrid-col-bg').forEach((el) => {
                el.style.background = 'transparent';
                el.style.backgroundColor = 'transparent';
            });

            // Time axis labels: keep at 13px — the column is narrow so larger sizes overflow.
            baseClone.querySelectorAll('.fc-timegrid-slot-label-cushion').forEach((el) => {
                el.style.setProperty('font-weight', '700', 'important');
                el.style.setProperty('font-size', '13px', 'important');
            });
            // Studio name headers have full column width — bump to 15px pre-scale.
            baseClone.querySelectorAll('.fc-col-header-cell-cushion').forEach((el) => {
                el.style.setProperty('font-size', '15px', 'important');
                el.style.setProperty('font-weight', '700', 'important');
            });

            // Horizontal pagination: split by columns (combination of day + studio)
            // Each column has data-date and data-resource-id
            // Structure: role="presentation" or td[role="gridcell"] with data-date and data-resource-id attributes
            
            // Collect all unique column combinations (date + studio)
            const columnMap = new Map(); // key: "date|resourceId", value: { date, resourceId }
            
            // Find all columns with data-date and data-resource-id
            const allColumns = baseClone.querySelectorAll('[data-date][data-resource-id]');
            allColumns.forEach((col) => {
                const date = col.getAttribute('data-date');
                const resourceId = col.getAttribute('data-resource-id');
                if (date && resourceId) {
                    const key = `${date}|${resourceId}`;
                    if (!columnMap.has(key)) {
                        columnMap.set(key, { date, resourceId: String(resourceId) });
                    }
                }
            });
            
            // Convert to array and sort by date, then by resourceId for consistency
            const allColumnKeys = Array.from(columnMap.keys()).sort((a, b) => {
                const [dateA, resourceA] = a.split('|');
                const [dateB, resourceB] = b.split('|');
                if (dateA !== dateB) {
                    return dateA.localeCompare(dateB);
                }
                return resourceA.localeCompare(resourceB);
            });
            
            // Determine the number of columns per page
            // In landscape A4 approximately 297mm width, accounting for margins - approximately 270mm
            // If each column is approximately 18-20mm, approximately 13-15 columns fit
            const maxColumnsPerPage = printConfigRef.current.maxColumnsPerPage; // Use config value
            const slotWidth = 80;

            // Day-aware pagination: never split a day across pages
            const uniqueDatesInOrder = [];
            const dayColumnGroups = {};
            allColumnKeys.forEach(k => {
                const date = k.split('|')[0];
                if (!dayColumnGroups[date]) {
                    dayColumnGroups[date] = [];
                    uniqueDatesInOrder.push(date);
                }
                dayColumnGroups[date].push(k);
            });
            const pages = [];
            let currentPageKeys = [];
            uniqueDatesInOrder.forEach(date => {
                const dayKeys = dayColumnGroups[date];
                if (currentPageKeys.length > 0 && currentPageKeys.length + dayKeys.length > maxColumnsPerPage) {
                    pages.push(currentPageKeys);
                    currentPageKeys = [...dayKeys];
                } else {
                    currentPageKeys = [...currentPageKeys, ...dayKeys];
                }
            });
            if (currentPageKeys.length > 0) pages.push(currentPageKeys);
            const totalPages = pages.length;

            const container = document.createElement('div');
            container.className = 'print-container';
            
            // Create main print style
            const printStyle = document.createElement('style');
            printStyle.textContent = `
                @page {
                    size: A4 ${printConfigRef.current.pageOrientation};
                }
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    margin: 0;
                    padding: 10mm;
                }
                @media print {
                    body {
                        margin: 0;
                        padding: 10mm;
                    }
                    .print-container {
                        width: 100%;
                    }
                    .fc-scroller, .fc-scroller-harness, .fc-scroller-harness-liquid,
                    .fc-scroller-liquid-absolute, .fc-timegrid-body, .fc-view-harness,
                    .fc-scrollgrid, .fc-timegrid { 
                        height: auto !important; 
                        max-height: none !important; 
                        overflow: visible !important; 
                    }
                    // .fc-scroller-harness {
                    //     height: auto !important;
                    //     max-height: none !important;
                    //     overflow: visible !important;
                    //     .fc-scroller{
                    //         height: auto !important;
                    //         max-height: none !important;
                    //         overflow: visible !important;
                    //     }
                    // }
                    thead {
                        display: table-row-group !important;
                    }
                    thead tr {
                        page-break-after: avoid !important;
                    }

                    .fc-timegrid-col-bg,
                    .fc-bg-event {
                        background: transparent !important;
                        background-color: transparent !important;
                    }
                    /* Clip grey tail — body height is set by JS to match visible slots */
                    .fc-timegrid-body {
                        overflow: hidden !important;
                    }
                    /* Let harness scroll content remain visible in print */
                    .fc-view-harness-active {
                        overflow: visible !important;
                    }
                    /* ── Hour-only lines: strip all slot borders, restore only on :00 ── */
                    .fc-timegrid-slot td {
                        border-top: none !important;
                        border-bottom: none !important;
                    }
                    .fc-timegrid-slot:not(.fc-timegrid-slot-minor) td {
                        border-top: 0.5px solid #d0d0d0 !important;
                    }
                    /* ── Bigger, bolder time labels ── */
                    .fc-timegrid-slot-label-cushion {
                        font-weight: 700 !important;
                        font-size: 13px !important;
                    }
                    /* ── Day headers: black, bold, no link style ── */
                    .fc-col-header-cell-cushion {
                        color: #2C2C2A !important;
                        font-weight: 700 !important;
                        text-decoration: none !important;
                        font-size: 13px !important;
                    }
                }
            `;
            container.appendChild(printStyle);

            let lastColSpan = 0;
            // Create pages: each page contains complete days (day-aware pagination)
            for (let page = 0; page < totalPages; page++) {
                const allowedColumnKeys = pages[page];
                const allowedColumns = new Set(allowedColumnKeys);

                const pageDiv = document.createElement('div');
                pageDiv.className = 'calendar-page';
                if (page < totalPages - 1) {
                    pageDiv.style.pageBreakAfter = 'always';
                    pageDiv.style.breakAfter = 'page';
                }

                // Clone calendar for this page
                const pageCalendar = baseClone.cloneNode(true);

                // Hide all columns that are not in allowedColumns
                // Check elements with role="presentation" and td[role="gridcell"]
                const columnsDayToCheck = pageCalendar.querySelectorAll('[data-date]:not([data-resource-id])');

                function hasStringWithSubstringInSet(set, substring) {
                    for (const item of set) {
                      if (item.includes(substring)) {
                        return true; // Match found, can return true immediately
                      }
                    }
                    return false; // Nothing found after iterating through all elements
                  }

                let countColspan = 0;
                let lastColSpanForPage = lastColSpan;
                columnsDayToCheck.forEach((el) => {
                    const date = el.getAttribute('data-date');
                    if (date) {
                        if (!hasStringWithSubstringInSet(allowedColumns, date)) {
                            el.remove();
                        } else {
                            if (countColspan === 0) {
                                el.colSpan = el.colSpan - lastColSpanForPage;
                            } else {
                                lastColSpanForPage = 0;
                            }
                            if (countColspan + el.colSpan <= maxColumnsPerPage) {
                                countColspan = countColspan + el.colSpan;
                            } else {
                                el.colSpan = maxColumnsPerPage - countColspan; 
                                lastColSpanForPage = el.colSpan + lastColSpanForPage;
                                countColspan = maxColumnsPerPage;
                            }
                        }
                    }
                });
                lastColSpan = lastColSpanForPage;
                if(totalPages > 1) {
                    pageCalendar.querySelectorAll('.fc-timegrid-slots table').forEach((el) => {
                            el.style.width = `${slotWidth * countColspan}px`;
                            el.style.minWidth = `0px`;
                    });

                } else {
                    pageCalendar.querySelectorAll('.fc-timegrid-body td').forEach((el) => {
                        el.style.width = `${1040 / countColspan}px`;
                    });
                    pageCalendar.querySelectorAll('th.fc-col-header-cell').forEach((el) => {
                        el.style.width = `${1040 / countColspan}px`;
                    });
                    pageCalendar.querySelectorAll('.fc-scroller-harness table[role="presentation"]').forEach((el) => {
                        if (' fc-scrollgrid-sync-table' !== el.className) {
                            el.style.width = `1040px`;
                        }
                    });
                    pageCalendar.querySelectorAll('.fc-timegrid-slots table').forEach((el) => {
                        el.style.width = `1040px`;
                    });
                    // Reset FullCalendar's screen-baked pixel widths so table fills the page
                    pageCalendar.querySelectorAll('.fc-scrollgrid').forEach((t) => {
                        t.style.width = '100%';
                        t.style.minWidth = '0';
                    });
                }

                const columnsToCheck = pageCalendar.querySelectorAll('[data-date][data-resource-id]');

                columnsToCheck.forEach((el) => {
                    const date = el.getAttribute('data-date');
                    const resourceId = el.getAttribute('data-resource-id');
                    if (date && resourceId) {
                        const key = `${date}|${resourceId}`;
                        if (!allowedColumns.has(key)) {
                            el.remove();
                        }
                    }
                });

                // Additionally: hide columns in headers and other places
                pageCalendar.querySelectorAll('[role="presentation"][data-date][data-resource-id]').forEach((el) => {
                    const date = el.getAttribute('data-date');
                    const resourceId = el.getAttribute('data-resource-id');
                    if (date && resourceId) {
                        const key = `${date}|${resourceId}`;
                        if (!allowedColumns.has(key)) {
                            el.remove();
                        }
                    }
                });

                // Hide table tbody cells
                pageCalendar.querySelectorAll('td[role="gridcell"][data-date][data-resource-id]').forEach((el) => {
                    const date = el.getAttribute('data-date');
                    const resourceId = el.getAttribute('data-resource-id');
                    if (date && resourceId) {
                        const key = `${date}|${resourceId}`;
                        if (!allowedColumns.has(key)) {
                            el.remove();
                        }
                    }
                });

                // Hide resource headers for hidden columns
                pageCalendar.querySelectorAll('.fc-col-header-cell').forEach((headerCell) => {
                    // Check all columns inside this header cell
                    const hasVisibleColumn = Array.from(headerCell.querySelectorAll('[data-date][data-resource-id]')).some(
                        (el) => {
                            const date = el.getAttribute('data-date');
                            const resourceId = el.getAttribute('data-resource-id');
                            if (date && resourceId) {
                                const key = `${date}|${resourceId}`;
                                return allowedColumns.has(key);
                            }
                            return false;
                        }
                    );
                    
                    // If this is a weekday cell (has .fc-col-header-cell-cushion), keep it visible
                    const dayHeaderCushion = headerCell.querySelector('.fc-col-header-cell-cushion');
                    if (!dayHeaderCushion && !hasVisibleColumn) {
                        // This is a studio/resource cell - hide if there are no visible columns
                        headerCell.style.display = 'none';
                        headerCell.style.visibility = 'hidden';
                    }
                });

                pageDiv.appendChild(pageCalendar);
                container.appendChild(pageDiv);
            }

            return container;
        },
        copyStyles: true,
        pageStyle: () => `@page {
            size: A4 ${printConfigRef.current.pageOrientation};
            @top-left-corner { content: ""; }
            @top-right-corner { content: ""; }
            @bottom-left-corner { content: ""; }
            @bottom-right-corner { content: ""; }
            margin: 0.19in 0.47in ${printConfigRef.current.marginBottom} 0.32in;
        }
        body {
            -webkit-print-color-adjust: exact; /* For Chrome/Safari */
            print-color-adjust: exact;         /* Standard property */
        }
        @media print {
            body {
                margin: 0;
                padding: 0;
            }
            .print-container {
                width: 100%;
            }
            .calendar-page {
                width: 100%;
                page-break-after: always;
                break-after: page;
            }
            .calendar-page:last-child {
                page-break-after: auto;
                break-after: auto;
            }
            .fc-scroller, .fc-scroller-harness, .fc-scroller-harness-liquid,
            .fc-scroller-liquid-absolute, .fc-timegrid-body, .fc-view-harness,
            .fc-scrollgrid, .fc-timegrid { 
                height: auto !important; 
                max-height: none !important; 
                overflow: visible !important; 
            }
            thead {
                display: table-row-group !important;
            }
            thead tr {
                page-break-after: avoid !important;
            }
        }`,
    });
    const handlePrintFull = useReactToPrint({
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                setSlotTime(TIME_SLOTS.MINUTES_15);
                setPrintMode(false);
                setTimeout(() => resolve(), 100);
            });
        },
        onAfterPrint: () => {
            setPrintMode(false);
        },
        content: () => {
            const baseClone = calendarRef.current.cloneNode(true);
            const headerLeftItem = baseClone.querySelectorAll('.fc-timegrid-axis-frame');

            headerLeftItem.forEach((el) => {
                const currentHeight = parseFloat(el.style.height || '0'); 
                el.style.height = `${currentHeight + currentHeight/6.66}px`;   
            });
            const header = baseClone.querySelectorAll('.fc-scrollgrid-sync-inner');

            header.forEach((el) => { 
                el.style.paddingLeft = `10px`;   
                el.style.paddingRight = `10px`;   
            });

            const bodyTbl = baseClone.querySelectorAll('[role="grid"]');
            bodyTbl.forEach((el) => {
                el.style.border = "none";
            });

            const bodyTBLRght = baseClone.querySelectorAll('[role="presentation"]');
            bodyTBLRght.forEach((el) => {
                el.style.borderRight = "none";
                el.style.borderBottom = "none";
            });

            // Common styling (as in FiltersSidebar)
            const harness = baseClone.getElementsByClassName("fc-scroller-harness fc-scroller-harness-liquid")[0];
            if (harness) {
                harness.style.overflow = "visible";
                const tableTimeRows = baseClone.querySelectorAll('table tbody tr');
                if (tableTimeRows && tableTimeRows.length > 0) {
                    for (let i = 0; i < tableTimeRows.length; i++) {
                        tableTimeRows[i].style.height = '7mm';
                    }
                }
            }
            const scroller = baseClone.getElementsByClassName("fc-scroller fc-scroller-liquid-absolute")[0];
            if (scroller) {
                scroller.style.overflow = "visible";
            }

            // Ensure no vertical scroll and allow full-height printing across pages
            const toAutoSelectors = [
                '.fc-scroller-harness',
                '.fc-scroller-harness-liquid',
                '.fc-scroller',
                '.fc-scroller-liquid-absolute',
                '.fc-timegrid-body table',
                '.fc-scrollgrid',
                '.fc-scrollgrid-sync-table',
                '.fc-view-harness',
                '.fc-timegrid',
                '.fc-timegrid-slots',
                '.fc-timegrid-cols'
            ];

            toAutoSelectors.forEach((sel) => {
                baseClone.querySelectorAll(sel).forEach((el) => {
                    el.style.maxHeight = 'none';
                    el.style.overflow = 'visible';
                });
            });

            // Reduce header height to compact as in FiltersSidebar
            const headerEl = baseClone.querySelector('.fc-col-header');
            if (headerEl) {
                // headerEl.style.height = '7mm';
            }

            // Show only weekday names in the date header (no numeric dates)
            const dayHeaderCells = baseClone.querySelectorAll('.fc-col-header thead tr:first-child .fc-col-header-cell-cushion');

            if (dayHeaderCells && dayHeaderCells.length >= 7) {
                Array.from(dayHeaderCells).forEach((cell, index) => {
                    const d = weekStart.clone().add(index, 'days');
                    cell.textContent = d.format('dddd');
                    cell.style.textTransform = 'capitalize';
                    cell.style.fontWeight = '700';
                    cell.style.fontSize = '14px';
                });
            }


            // Adjust time slot row heights and borders
            const tableTimeRows = baseClone.querySelectorAll('.fc-timegrid-slots table tbody tr');

            if (tableTimeRows && tableTimeRows.length > 0) {
                let startTimeDayCell = tableTimeRows[0].querySelector('td');
                let startTimeDay = startTimeDayCell ? parseFloat(startTimeDayCell.getAttribute('data-time')) : 0;

                for (let i = 0; i < tableTimeRows.length; i++) {
                    tableTimeRows[i].style.height = '7mm';
                }
                // Hour lines only — clear ALL fc-timegrid-slot rows (label + grid cols), restore hourly
                baseClone.querySelectorAll('.fc-timegrid-slot').forEach((row) => {
                    const isMajor = !row.classList.contains('fc-timegrid-slot-minor');
                    row.querySelectorAll('td').forEach((cell) => {
                        cell.style.setProperty('border-top', isMajor ? '0.5px solid #d0d0d0' : 'none', 'important');
                        cell.style.setProperty('border-bottom', 'none', 'important');
                    });
                });

                // Border under studio name headers — box-shadow bypasses border-collapse.
                baseClone.querySelectorAll('.fc-col-header-cell').forEach(el => {
                    el.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });

                // Border at the very bottom of the grid.
                baseClone.querySelectorAll('.fc-timegrid-slots table tbody tr:last-child td').forEach(td => {
                    td.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });
                baseClone.querySelectorAll('.fc-timegrid-cols table tbody tr:last-child td').forEach(td => {
                    td.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });
                // Adjust stickers sizing/positioning
                const stickers = baseClone.getElementsByClassName('fc-timegrid-event-harness fc-timegrid-event-harness-inset');
                for (let i = 0; i < stickers.length; i++) {
                    const sticker = stickers[i].getElementsByClassName('test')[0];
                    if (!sticker) continue;
                    const lengthAttr = sticker.getAttribute('data-length');
                    const timeStartStickerAttr = sticker.getAttribute('data-time');
                    const length = lengthAttr ? parseFloat(lengthAttr) : 0;
                    const timeStartSticker = timeStartStickerAttr ? parseFloat(timeStartStickerAttr) : startTimeDay;
                    const innerDiv = sticker.querySelector('div');
                    if (innerDiv) innerDiv.style.overflow = 'hidden';

                    // Font: cap at 13px, scale down only if card height can't fit all items
                    {
                        // Select ALL card row divs (title, teacher, time).
                        // ClassCard: sticker > [aria-hidden overlays] + .card > [rows]
                        // The time row has no data-id so querySelectorAll('[data-id]') misses it.
                        // Skip aria-hidden overlays to reach the .card div, grab its DIV children.
                        const cardDiv = Array.from(sticker.children).find(
                            el => el.tagName === 'DIV' && !el.hasAttribute('aria-hidden')
                        );
                        const allCardItems = cardDiv
                            ? Array.from(cardDiv.children).filter(el => el.tagName === 'DIV')
                            : Array.from(sticker.querySelectorAll('[data-id="card-item"]'));
                        const numItems = Math.max(1, allCardItems.length);
                        const cardHeightMm = length * 28; // 4 slots/hr × 7mm/slot
                        const mmPerLine = 5.98; // 13px × 0.353mm/px × 1.3 lineHeight
                        const linesAvail = Math.max(0.5, (cardHeightMm - 2) / mmPerLine);
                        const rawSize = 13 * Math.min(1, linesAvail / numItems);
                        const printFontSize = `${Math.max(7.5, rawSize).toFixed(1)}px`;
                        const printLineH = rawSize >= 11 ? '1.3' : '1.2';
                        allCardItems.forEach((item) => {
                            item.style.setProperty('font-size', printFontSize, 'important');
                            item.style.setProperty('line-height', printLineH, 'important');
                        });
                    }
                    if (length >= 1.5) {
                        sticker.querySelectorAll('[data-id="card-item"]').forEach((item) => {
                            item.style.overflow = 'auto';
                            item.style.display = 'block';
                            item.style.webkitLineClamp = 'initial';
                            item.style.webkitBoxOrient = 'horizontal';
                            item.style.wordBreak = 'normal';
                        });
                    }

                    stickers[i].style.height = `${length * DEFAULT_CARD_HEIGHT}mm`;
                    const countPoint = (timeStartSticker > startTimeDay ? timeStartSticker - startTimeDay : startTimeDay - timeStartSticker) / 0.2495;
                    stickers[i].style.top = `${countPoint !== 0 ? (STICKER_STYLE_TOP_RATIO + ((countPoint - 1) * STICKER_STYLE_TOP_RATIO)) : 0}mm`;
                    stickers[i].style.bottom = '0mm';
                }
            }

            // Clip timegrid body to visible slot height — preserves internal grey, hides grey tail
            const timegridBody = baseClone.querySelector('.fc-timegrid-body');
            if (timegridBody && tableTimeRows.length > 0) {
                const totalMm = tableTimeRows.length * 7;
                timegridBody.style.height = `${totalMm}mm`;
                timegridBody.style.maxHeight = `${totalMm}mm`;
                timegridBody.style.overflow = 'hidden';
            }
            baseClone.querySelectorAll('.fc-timegrid-col-bg').forEach((el) => {
                el.style.background = 'transparent';
                el.style.backgroundColor = 'transparent';
            });

            // Time axis labels: keep at 13px — the column is narrow so larger sizes overflow.
            baseClone.querySelectorAll('.fc-timegrid-slot-label-cushion').forEach((el) => {
                el.style.setProperty('font-weight', '700', 'important');
                el.style.setProperty('font-size', '13px', 'important');
            });
            // Studio name headers have full column width — bump to 15px pre-scale.
            baseClone.querySelectorAll('.fc-col-header-cell-cushion').forEach((el) => {
                el.style.setProperty('font-size', '15px', 'important');
                el.style.setProperty('font-weight', '700', 'important');
            });

            // Return full calendar without pagination - print everything on one page
            // Calculate number of columns (date + studio combinations)
            const columnMap = new Map();
            const allColumns = baseClone.querySelectorAll('[data-date][data-resource-id]');
            allColumns.forEach((col) => {
                const date = col.getAttribute('data-date');
                const resourceId = col.getAttribute('data-resource-id');
                if (date && resourceId) {
                    const key = `${date}|${resourceId}`;
                    if (!columnMap.has(key)) {
                        columnMap.set(key, { date, resourceId: String(resourceId) });
                    }
                }
            });
            
            const totalColumns = columnMap.size;
            
            // Calculate dynamic scale factor based on BOTH width AND height
            // A4 landscape: ~297mm width, ~210mm height (minus margins)
            // Available print area: ~270mm width, ~190mm height (with minimal margins)
            const columnsPerPage = 19; // Approximate number of columns that fit on A4 landscape width
            let scaleFactorWidth = columnsPerPage / totalColumns;
            
            // Use actual calendar scroll width for a precise fit calculation.
            // A4 landscape minus margins ≈ 1020px at 96dpi.
            const calendarScrollWidth = calendarRef.current ? calendarRef.current.scrollWidth : 0;
            const TARGET_PRINT_PX = 1020;
            let scaleFactor = calendarScrollWidth > 0
                ? Math.min(1.0, TARGET_PRINT_PX / calendarScrollWidth)
                : Math.max(0.05, Math.min(1.0, scaleFactorWidth));
            scaleFactor = Math.max(0.05, scaleFactor);
            // Remove any margins/padding from the calendar clone
            if (baseClone.style) {
                baseClone.style.margin = '0';
                baseClone.style.padding = '0';
            }
            
            // Apply page-break-inside: avoid to ALL nested elements to prevent any breaks
            const allElements = baseClone.querySelectorAll('*');
            allElements.forEach((el) => {
                el.style.pageBreakInside = 'avoid';
                el.style.breakInside = 'avoid';
            });
            
            // Create wrapper to control scaling
            const wrapper = document.createElement('div');
            wrapper.className = 'print-wrapper';
            wrapper.style.width = 'fit-content';
            wrapper.style.height = 'fit-content';
            wrapper.style.overflow = 'visible';
            wrapper.style.position = 'relative';
            wrapper.style.left = '0';
            wrapper.style.top = '0';
            wrapper.style.margin = '0';
            wrapper.style.padding = '0';
            wrapper.style.display = 'inline-block'; // Make it atomic for page breaking
            wrapper.style.zoom = scaleFactor.toString();
            wrapper.style.pageBreakInside = 'avoid';
            wrapper.style.breakInside = 'avoid';
            wrapper.appendChild(baseClone);
            
            // Create dynamic style for print
            const dynamicStyle = document.createElement('style');
            dynamicStyle.textContent = `
                @media print {
                    .print-container {
                        position: relative !important;
                        left: 0 !important;
                        top: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .print-container .print-wrapper {
                        position: relative !important;
                        left: 0 !important;
                        top: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        display: inline-block !important;
                        zoom: ${scaleFactor} !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        orphans: 999 !important;
                        widows: 999 !important;
                    }
                    .print-container .print-wrapper * {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }

                    .fc-timegrid-col-bg,
                    .fc-bg-event {
                        background: transparent !important;
                        background-color: transparent !important;
                    }
                    /* Clip grey tail — body height is set by JS to match visible slots */
                    .fc-timegrid-body {
                        overflow: hidden !important;
                    }
                    /* Let harness scroll content remain visible in print */
                    .fc-view-harness-active {
                        overflow: visible !important;
                    }
                    /* ── Hour-only lines: strip all slot borders, restore only on :00 ── */
                    .fc-timegrid-slot td {
                        border-top: none !important;
                        border-bottom: none !important;
                    }
                    .fc-timegrid-slot:not(.fc-timegrid-slot-minor) td {
                        border-top: 0.5px solid #d0d0d0 !important;
                    }
                    /* ── Bigger, bolder time labels ── */
                    .fc-timegrid-slot-label-cushion {
                        font-weight: 700 !important;
                        font-size: 13px !important;
                    }
                    /* ── Day headers: black, bold, no link style ── */
                    .fc-col-header-cell-cushion {
                        color: #2C2C2A !important;
                        font-weight: 700 !important;
                        text-decoration: none !important;
                        font-size: 13px !important;
                    }
                }
            `;
            
            const container = document.createElement('div');
            container.className = 'print-container';
            container.style.position = 'relative';
            container.style.left = '0';
            container.style.top = '0';
            container.style.margin = '0';
            container.style.padding = '0';
            container.style.display = 'inline-block';
            container.style.pageBreakInside = 'avoid';
            container.style.breakInside = 'avoid';
            container.appendChild(dynamicStyle);
            container.appendChild(wrapper);
            return container;
        },
        copyStyles: true,
        pageStyle: () => `@page {
            @top-left-corner { content: ""; }
            @top-right-corner { content: ""; }
            @bottom-left-corner { content: ""; }
            @bottom-right-corner { content: ""; }
            margin: 0;
            // size: A4 landscape;
        }
        body {
            -webkit-print-color-adjust: exact; /* For Chrome/Safari */
            print-color-adjust: exact;         /* Standard property */
            margin: 0 !important;
            padding: 0 !important;
            position: relative !important;
        }
            html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                height: 100% !important;
                overflow: visible !important;
                position: relative !important;
            }
            .fc-col-header-cell-cushion, .fc-sticky {
                left: auto !important;
                right: auto !important;
            }
            .print-container {
                position: relative !important;
                left: 0 !important;
                top: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                height: 100% !important;
                overflow: visible !important;
                display: inline-block !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                orphans: 999 !important;
                widows: 999 !important;
            }
            .print-container .print-wrapper {
                position: relative !important;
                left: 0 !important;
                top: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                overflow: visible !important;
                width: fit-content !important;
                height: fit-content !important;
                display: inline-block !important;
                orphans: 999 !important;
                widows: 999 !important;
            }
            .print-container .print-wrapper > * {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                orphans: 999 !important;
                widows: 999 !important;
            }
            .print-container .print-wrapper * {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            .fc-scroller, .fc-scroller-harness, .fc-scroller-harness-liquid,
            .fc-scroller-liquid-absolute, .fc-timegrid-body, .fc-view-harness,
            .fc-scrollgrid, .fc-timegrid { 
                height: auto !important; 
                max-height: none !important; 
                overflow: visible !important; 
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            thead {
                display: table-row-group !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            thead tr {
                page-break-after: avoid !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            tbody {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            tr {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            td, th {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            table {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            .print-container div,
            .print-container .fc-timegrid-event-harness,
            .print-container .fc-timegrid-col,
            .print-container .fc-timegrid-cols,
            .print-container .fc-timegrid-slots {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }`,
    });
    const exportWeekViewLandscape = () => {
        printConfigRef.current = { maxColumnsPerPage: 19, pageOrientation: 'landscape', marginBottom: '0.32in' };
        handlePrint();
    };

    const exportWeekViewFull = () => {
        handlePrintFull();
    };

    // ── Export panel helpers ───────────────────────────────────────────────




    if (loading) {
        return (
            <div className={styles.weekView}>
                <div className={styles.loadingContainer}>
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading week schedule...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.weekView}>
            <div className={styles.weekWrapper}>
                {/* Toolbar — Schedule dropdown → Filter → Color by → Print options → Fine-tune | Close Week View right */}
                <div className={styles.weekHeader}>
                    {/* Left group: Schedule → Close Week View → Color by → Fine-tune → Filter */}
                    <div className={styles.weekActions}>
                        <Select
                            components={{ Control: ({ children, ...props }) => (
                                <SelectComponents.Control {...props}><ScheduleIcon style={{ marginLeft: 8, flexShrink: 0 }} />{children}</SelectComponents.Control>
                            )}}
                            className="schedulesSelectButton"
                            classNamePrefix="schedulesSelect"
                            value={board}
                            onChange={(value) => {
                                dispatch(addChosenBoard(value));
                                if (onBoardChange) onBoardChange(value);
                            }}
                            options={boardOptions}
                            styles={{ container: (base) => ({ ...base, minWidth: '200px', width: 'auto', maxWidth: '280px', flexShrink: 0 }) }}
                        />
                        <button className={[styles.editButton, styles.closeButton].join(' ')} onClick={onClose}>
                            <ScheduleIcon className="me-2" /> Close Week View
                        </button>
                        <ColorBySelect
                            propColorBy={currentColorBy}
                            handleColorByChange={handleColorByChange}
                        />
                        <OverlayTrigger placement="bottom" overlay={<Tooltip id="wv-scale-tip">Change snap precision for drag &amp; drop</Tooltip>}>
                            <button className={[styles.editButton, styles.scaleButton].join(' ')} onClick={onChangeDayMinWidth}>
                                {dayMinWidth === DAY_MIN_WIDTH.LITTLE_WIDTH ? <ZoomInIcon /> : <ZoomOutIcon />}
                                <p>{dayMinWidth === DAY_MIN_WIDTH.LITTLE_WIDTH ? '5 min' : '15 min'}</p>
                            </button>
                        </OverlayTrigger>
                        <div style={{ position: 'relative' }}>
                            <ToggleButton
                                className="filterButton greyButton my-1 mx-1"
                                id="week-toggle-filter"
                                type="checkbox"
                                variant="outline-primary"
                                checked={filterModal}
                                value="filterModal"
                                onChange={() => setFilterModal(!filterModal)}
                            >
                                <FilterIcon className="me-2" /> Filter
                            </ToggleButton>
                            <DialogModal
                                isOpen={filterModal}
                                onClose={() => setFilterModal(false)}
                            />
                        </div>
                    </div>
                    {/* Right group: Export */}
                    <div className={styles.weekActionsRight}>
                        <ExportPanel
                            isWeekView
                            board={board}
                            onPdfExport={exportWeekViewLandscape}
                            onPdfFitExport={exportWeekViewFull}
                            userId={isAdmin ? params?.id : id}
                        />
                    </div>
                </div>
                {Object.keys(activeFilters).length > 0 && filtersList()}

            </div>
            <div ref={calendarRef} className={studios.length > 3 ? styles.calendar32 : styles.calendar21}>
                <FullCalendar
                    ref={calendarRef}
                    aspectRatio={1.9}
                    slotMinTime={slotMinTime}
                    slotMaxTime={slotMaxTime}
                    datesAboveResources={true}
                    schedulerLicenseKey={'CC-Attribution-NonCommercial-NoDerivatives'}
                    events={weekEvents}
                    resources={studios}
                    resourceOrder={null}
                    duration={{ days: 7 }}
                    dayHeaderFormat={{ weekday: 'long' }}
                    headerToolbar={{
                        left: '',
                        center: '',
                        right: ''
                    }}
                    hiddenDays={hiddenDays}
                    plugins={[resourceTimeGridPlugin, timeGridPlugin, interactionPlugin, adaptivePlugin, scrollgridPlugin]}
                    initialView='resourceTimeGrid'
                    initialDate={weekStart.format('YYYY-MM-DD')}
                    defaultTimedEventDuration={'01:00'}
                    editable={true}
                    selectable={false}
                    droppable={false}
                    dragRevertDuration={0}
                    slotDuration={slotTime}
                    selectMirror={true}
                    dayMaxEvents={true}
                    allDaySlot={false}
                    slotLabelInterval={'01:00'}
                    slotLabelFormat={{ hour: 'numeric', minute: '2-digit', meridiem: false }}
                    eventTimeFormat={{
                        hour: "numeric",
                        minute: "2-digit",
                        meridiem: false
                    }}
                    eventContent={ClassCard}
                    eventClick={handleEventClick}
                    eventChange={handleEvents}
                    eventDurationEditable={false}
                    eventOverlap={false}
                    eventColor={'#f2f2f2'}
                    dayMinWidth={dayMinWidth}
                    businessHours={businessHours}
                    eventConstraint="businessHours"
                />
            </div>
        </div>
    );
};

export default WeekView;

